(ns pfo.activity.component
  (:require [reagent.core :as reagent :refer [atom]]
            [goog.string :refer [format]]
            [promesa.core :as p]
            [pfo.api :as api :refer [process-error]]
            [pfo.assignment.component :refer [assignments-table assignment-list]]
            [pfo.util :as util]
            [pfo.tz :as tz]
            [pfo.activity.model :as model]
            [pfo.revroutes :refer [path-for]]
            [pfo.widget.paginator :refer [paginator tfoot-paginator]]
            [pfo.state :as state]
            [pfo.task.model :as task-model]))

(declare activity)

(def object-type-task "t")
(def object-type-task-occurrence "to")
(def object-type-event "e")
(def object-type-event-occurrence "eo")
(def object-type-task-group "tg")
(def object-type-document "d")
(def object-type-note "n")
(def object-type-collection "col")
(def object-type-user "u")
(def object-type-comment "com")
(def object-type-fincen-submission "fs")

(def action-type-add "add")
(def action-type-update "update")
(def action-type-delete "delete")
(def action-type-rollback "rollback")
(def action-type-invite "invite")
(def action-type-notify "notify")
(def action-type-reply "reply")
(def action-type-toggle-complete "toggle_complete")
(def action-type-download "download")
(def action-type-submit "submit")
(def action-type-signature-request-sent "signature_request_sent")
(def action-type-signature-remind "signature_remind")
(def action-type-signature-remind-all "signature_remind_all")
(def action-type-signature-signer-complete "signature_signer_complete")
(def action-type-signature-complete "signature_complete")
(def action-type-signature-cancel "signature_cancel")
(def action-type-reply-from-inbox "reply_from_inbox")
(def action-type-decline "decline")
(def action-type-collection-review-discard "collection_review_discard")
(def action-type-collection-review-delete "collection_review_delete")
(def action-type-collection-review-rerun "collection_review_rerun")

(def object-type-name
  {object-type-task "task"
   object-type-task-occurrence "task"
   object-type-event "event"
   object-type-event-occurrence "event"
   object-type-task-group "task group"
   object-type-document "document"
   object-type-note "document"
   object-type-collection "collection"
   object-type-user "user"
   object-type-comment "comment"
   object-type-fincen-submission "Boir E-Filing"})

(defn task-link [id] (str "#/task/details/" id "/"))
(defn task-occurrence-link [id complete-by] (str "#/task/details/" id "/" complete-by "/"))
(defn task-group-link [task-group-id] (str "#/task/group/" task-group-id "/details/"))
(defn event-link [id] (str "#/event/details/id/" id "/"))
(defn event-occurrence-link [id akey] (str "#/event/details/" akey "/"))
(defn document-link [id] (str "#/document/details/" id))
(defn note-link [id] (str "#/document/details/" id))
(defn occurrence-link [id] (str "#/occurrence/details/" id "/"))

(def action-verbs
  {action-type-add "added"
   action-type-update "updated"
   action-type-delete "deleted"
   action-type-rollback "deleted"
   action-type-invite "was invited to"
   action-type-notify "was notified about"
   action-type-reply "replied to"
   action-type-download "downloaded"
   action-type-submit "submited"})

(def document-type-boir "b")

(defn ai-suffix [action]
  (when (= "true" (get-in action [:data :ai]))
    " with AARK™"))

(defn task-activity-item [options action]
  (let [compliance? (= "1" (get-in action [:data :compliance]))
        signature (get-in action [:data :signature])
        signature-updated? (= "1" (get-in action [:data :signature_updated])) 
        dropbox-sign-signature? (or (= signature task-model/signature-type-dropbox-sign-delete) 
                                    (= signature task-model/signature-type-dropbox-sign-retain))]
    (condp = (:action_type action)
      action-type-toggle-complete [:span "marked " (if compliance? "compliance" "task") " " (if (= "1" (get-in action [:data :complete])) "complete" "incomplete") (ai-suffix action)
                                   ": " [:a {:href (task-occurrence-link (:target_id action) (get-in action [:data :complete_by]))} (get-in action [:data :name])]]
      ,
      action-type-signature-request-sent [:span [:a {:href (task-occurrence-link (:target_id action) (get-in action [:data :complete_by]))} "Signature request sent"]]
      ,
      action-type-signature-signer-complete [:span
                                             [:a {:href (task-occurrence-link (:target_id action) (get-in action [:data :complete_by]))}
                                              "Signer completed signature ("
                                              (get-in action [:data :signer_complete_count])
                                              " of "
                                              (get-in action [:data :signer_count]) ")"]]
      ,
      action-type-signature-remind [:span
                                    [:a {:href (task-occurrence-link (:target_id action) (get-in action [:data :complete_by]))}
                                     (str "Sent " (if (= "true" (get-in action [:data :automatic])) "automatic" "manual")" signature reminder to ")
                                     (get-in action [:data :signer_name])
                                     " <"
                                     (get-in action [:data :signer_email])
                                     ">"]]
      ,
      action-type-signature-complete [:span [:a {:href (task-occurrence-link (:target_id action) (get-in action [:data :complete_by]))}
                                             "Signature complete"]]
      ,
      action-type-signature-cancel [:span [:a {:href (task-occurrence-link (:target_id action) (get-in action [:data :complete_by]))} "Signature cancelled"]]
      ,
      action-type-decline [:span "Declined: "
                           [:a {:href (task-occurrence-link (:target_id action) (get-in action [:data :complete_by]))}
                            (get-in action [:data :name])]]
      ,
      [:span (action-verbs (:action_type action)) " "
       (if (= "1" (get-in action [:data :delete_step]))
         (if compliance?
           "a compliance step"
           (case (get-in action [:data :multi_step])
             "1" "a multi-step task step"
             "0" "a task"))
         (if compliance? "a compliance" "a task"))
       [:span ": "
        (if-not (= action-type-delete (:action_type action))
          [:a {:href (task-link (:target_id action))} (get-in action [:data :name])]
          (get-in action [:data :name]))]
       (when (and dropbox-sign-signature? signature-updated?)
         (condp = signature 
           task-model/signature-type-dropbox-sign-delete ": Dropbox Sign (delete unsigned) selected"
           task-model/signature-type-dropbox-sign-retain ": Dropbox Sign (retain unsigned) selected"))
       (when-not (empty? (get-in action [:data :bulk_edit_id])) " (bulk edit)")])))

(defn task-occurrence-activity-item [options action]
  [:span (action-verbs (:action_type action)) " a task"
   [:span  ": "
    (if-not (= action-type-delete (:action_type action))
      [:a {:href (task-occurrence-link (:target_id action) (get-in action [:data :complete_by]))} (get-in action [:data :name])]
      (get-in action [:data :name])) (when-not (empty? (get-in action [:data :bulk_edit_id])) " (bulk edit)")]])

(defn event-activity-item [options action]
  [:span (action-verbs (:action_type action)) " an event"
   [:span  ": "
    (if-not (= action-type-delete (:action_type action))
      [:a {:href (event-link (:target_id action))} (get-in action [:data :name])]
      (get-in action [:data :name]))]])

(defn event-occurrence-activity-item [options action]
  [:span (action-verbs (:action_type action)) " an event"
   [:span  ": "
    (if-not (= action-type-delete (:action_type action))
      [:a {:href (event-occurrence-link (:target_id action) (get-in action [:data :key]))} (get-in action [:data :name])]
      (get-in action [:data :name]))]])

(defn document-activity-item [options action]
  (if (= "true" (get-in action [:data :summary]))
    [:span "created a document summary"
     [:span ": "
      [:a {:href (document-link (:target_id action))} (get-in action [:data :name])]]]
    [:span (action-verbs (:action_type action))
     (condp = (get-in action [:data :doc_type])
       document-type-boir " a BOIR document"
       " a document")
     (ai-suffix action)
     [:span ": "
      (if-not (contains? #{action-type-delete action-type-rollback} (:action_type action))
        [:a {:href (document-link (:target_id action))} (get-in action [:data :name])]
        (get-in action [:data :name]))
      (when (= action-type-rollback (:action_type action))
        " (transaction rollback)")]]))

(defn comment-activity-item [options action]
  (let [commented-object-type (get-in action [:data :object_type])
        commented-object-id (get-in action [:data :object_id])
        body (util/ellipsis (get-in action [:data :plaintext_body]) 10)
        noun (if (= "1" (get-in action [:data :note])) "note" "comment")]
    [:span 
     (condp = (:action_type action)
       action-type-add (format "added a %s: " noun)
       action-type-update (format "edited a %s: " noun)
       action-type-delete "deleted "
       action-type-notify "was notified about: "
       action-type-reply "posted a reply: "
       action-type-reply-from-inbox "replied from inbox: ")
     [:a {:href (condp = commented-object-type
                  object-type-task (task-link commented-object-id)
                  object-type-task-occurrence (task-occurrence-link
                                                ;; In newer occurence entries, there should be task_id key, and object_id is occurrence ID
                                                (if-let [task-id (get-in action [:data :task_id])]
                                                  task-id
                                                  commented-object-id)
                                                (get-in action [:data :complete_by]))
                  object-type-event (event-link commented-object-id)
                  object-type-event-occurrence (event-occurrence-link commented-object-id (get-in action [:data :key]))
                  object-type-document (document-link commented-object-id)
                  object-type-note (note-link commented-object-id))}
      (condp = (:action_type action)
        action-type-delete (str "a " noun)
        action-type-notify (if (= "delete" (get-in action [:data :action]))
                             "deleted comment"
                             body)
        body)]]))

(defn task-group-activity-item [options action]
  [:span (action-verbs (:action_type action))
   " "
   (case (get-in action [:data :task_object_type])
     "t" (case (get-in action [:data :compliance])
           "1" [:a {:href (task-group-link (:target_id action))} "a compliance"]
           "0" (case (get-in action [:data :multi_step])
                 "1" "a multi-step task"
                 "0" "a task"))
     "e" "an event") (ai-suffix action)
   (when-not (empty? (get-in action [:data :bulk_edit_id])) " (bulk edit)")
   (when (= action-type-rollback (:action_type action))
     " (transaction rollback)")])


(defn fallback-activity-item [options action]
  (let [target-type (:target_type action)
        type-name (object-type-name (:target_type action))
        type-name (if (contains? #{object-type-event object-type-event-occurrence} target-type)
                    (str "an " type-name)
                    (str "a " type-name))]
    [:span 
     (action-verbs (:action_type action))
     " " type-name (ai-suffix action)]))

(defn collection-details-activity-item [options action]
  (let [data (:data action)]
    [:span
     (str (:collection_details_group_label data))
     (if (:task_group_id data)
       [:span " of collection details was modified with " [:a {:href (path-for :task-group-details :id (:task_group_id data))} "review task"] " selected."]
       " of collection details was modified. Review task was not selected.")]))

(defn collection-activity-item [options action]
  (let [activity-item (cond
                        (contains? (:data action) :collection_details_group_label) collection-details-activity-item
                        (= action-type-collection-review-discard (:action_type action)) (constantly [:span "discarded a collection review draft"])
                        (= action-type-collection-review-delete (:action_type action)) (constantly [:span "deleted a collection review report"])
                        (= action-type-collection-review-rerun (:action_type action)) (constantly [:span "re-ran a collection review, replacing its draft"])
                        :else fallback-activity-item)]
    (activity-item options action)))

(def submission-initiated    "submission_initiated")
(def submission-processing   "submission_processing")
(def submission-validation-passed "submission_validation_passed")
(def submission-validation-failed "submission_validation_failed")
(def submission-accepted     "submission_accepted")
(def submission-rejected     "submission_rejected")
(def submission-failed       "submission_failed")

(def submision-status
  {submission-initiated "Initiated"
   submission-processing "Processing"
   submission-validation-passed "Validation Passed"
   submission-validation-failed "Validation Failed"
   submission-accepted "Accepted"
   submission-rejected "Rejected"
   submission-failed "Failed"})

(def id-upload-status
  {"upload_success" "Success"
   "upload_failed" "Failed"})

(defn fincen-submission-activity-item [options action]
  [:span
   [:a {:href (occurrence-link (get-in action [:data :occurrence_id]))} (object-type-name (:target_type action))] 
   ": " (if (= action-type-download (:action_type action))
          [:a {:href (document-link (get-in action [:data :transcript_document_id]))} "Transcript Received"]
          (submision-status (get-in action [:data :status])))])

(def object-type-components
  {
   object-type-task task-activity-item
   object-type-task-occurrence task-occurrence-activity-item
   object-type-event event-activity-item
   object-type-event-occurrence event-occurrence-activity-item
   object-type-document document-activity-item
   object-type-note document-activity-item
   object-type-comment comment-activity-item
   object-type-task-group task-group-activity-item
   object-type-collection collection-activity-item
   object-type-fincen-submission fincen-submission-activity-item
   })

(defn collection-activity [{collection-id :collection-id :as options}]
  (let [actions (atom nil)
        current-page (atom 1)
        load-activity (fn [page]
                        (reset! current-page page)
                        (->> (model/collection-activity {:collection-id collection-id
                                                         :include-subcollections @state/include-descendants
                                                         :page page})
                             (p/map #(reset! actions %))
                             (process-error)))]
    (load-activity @current-page)
    (fn []
      [activity @actions load-activity current-page])))

(defn user-activity [{}]
  (let [actions (atom nil)
        current-page (atom 1)
        load-activity (fn [page]
                        (reset! current-page page)
                        (->> (model/user-activity page)
                             (p/map #(reset! actions %))
                             (process-error)))]
    (load-activity @current-page)
    (fn []
      [activity @actions load-activity current-page])))

(defn get-assignments [action]
  (mapv (fn [col] {:collection_id (:id col)
                   :collection col})
        (:collections action)))

(defn activity [actions load-activity current-page]

  (if (seq (:results actions))
    [:table.activity.t-table
     [:thead
      [:tr
       [:th.date "Date"]
       [:th.who "Who"]
       [:th.action "Action"]
       [:th.assignments "Collections"]]]
     [:tbody
      (doall
        (for [action (:results actions)]
          [:tr {:key (:id action)}
           [:td.date (-> (:created_at action) (util/parse-datetime) (tz/to-browser-time) (util/date-format-date-hour-minute))]
           [:td.who (:actor_first_name action) " " (:actor_last_name action)]
           [:td.action [(get object-type-components (:target_type action) fallback-activity-item) nil action]]
           [:td.assignments [assignment-list (get-assignments action)]]]))]

     [tfoot-paginator
      [paginator 
       (map (fn [page-number]
              {:number page-number
               :on-click (util/handler #(load-activity page-number))})
            (:page_numbers actions))
       @current-page
       (when (:has_previous actions) (util/handler #(load-activity (- @current-page 1))))
       (when (:has_next actions) (util/handler #(load-activity (+ @current-page 1))))] 
      4 
      (-> (:page_numbers actions) count (> 1))]]

    [:p.o-media "No actions."]))
